import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { checkoutPaymentClient, commonClient } from "../../core/api";
import {
  startLoading,
  endLoading,
} from "../../components/basic/LoadingProgress/loadingProgressSlice";
import { showNotification } from "@/components/basic/NotificationBar/notificationBarSlice";
import { getErrorMessage } from "../../core/helpers/errorHelper";

export interface CheckoutPaymentState {
  formArr: {
    registration: string;
    transfer_type: string;
    acrylic_plate_total: string;
    CreditCardFee: string;
    delivery_type: string;
    gift_wrapping: string;
    order_plate: string;
    creditcardfee: string;
    display_price: string;
    display_vat: string;
    title: string;
    forename: string;
    surname: string;
    FormID: string;
    digit_style: string;
    border: string;
    euro: string;
    sourcecode: string;
    finance: boolean;
    plate_size: string;
  } | null;
  registrationDetail: {
    registration: string;
    sourcecode: string;
    price: string;
    transfer_fee: string;
    vat: string;
    originalPrice: string;
    web_vat: number;
    display_price: string;
    price_inc_transfer: string;
    display_price_inc_transfer: string;
    display_vat: string;
    remainingPayment: string;
    discounted_web_admin_fee: string;
    premiumCharge: string;
  } | null;
  financeOptions: {
    [key: string]: {
      title: string;
      group: string;
      name: string;
      term: string;
      interest: number;
      productId: string;
      productGuid: string;
      maxLoan: number;
      minLoan: number;
      monthlyRate: number;
      calculationFactor: number;
      documentFee: number;
      documentFeePercentage: number;
      documentFeeMinimum: number;
      documentFeeMaximum: number;
      documentFeeCollectionMonth: number;
      serviceFee: number;
      settlementFee: number;
      deferredPeriod: number;
      optionPeriod: number;
      altTag: string;
      description: string;
    };
  };
  financePrice: number;
  creditInformation: {
    apr: number;
    loan_cost: string;
    loan_repayment: string;
    payment_regular: string;
    payment_regular_detail: string;
    annual_rate: string;
  };
  cardFeeArr: {
    price: string;
    initialPayment: number;
    remainingPayment: string;
    amexFee: number;
    creditFee: string;
    maestroFee: number;
  };
  calcPrice: number;
  dekoOptionSelected: string;
  cardType: string;
  loanDeposit: number;
  plateFixture: string;
  plateFixtureValue: number;
  pricingArr: {
    deliveryPrice: number;
  };
  fetchingDetailStatus: boolean;
  forceDepositMax: boolean;
  maximumDeposit: number;
  creditCardMessage: string;
  threeDSAuthData:
    | undefined
    | {
        status: string;
        CReq?: string;
        PARes?: string;
        MD?: string;
        TermUrl?: string;
        ACSURL: string;
      };
  isSubmitting: boolean;
}

const initialState: CheckoutPaymentState = {
  formArr: null,
  registrationDetail: null,
  financeOptions: {},
  financePrice: 0,
  creditInformation: {
    apr: 0,
    loan_cost: "",
    loan_repayment: "",
    payment_regular: "",
    payment_regular_detail: "",
    annual_rate: "",
  },
  cardFeeArr: {
    price: "",
    initialPayment: 0,
    remainingPayment: "",
    amexFee: 0,
    creditFee: "",
    maestroFee: 0,
  },
  calcPrice: 0,
  dekoOptionSelected: "",
  cardType: "",
  loanDeposit: 30,
  plateFixture: "",
  plateFixtureValue: 0,
  pricingArr: {
    deliveryPrice: 0,
  },
  fetchingDetailStatus: false,
  forceDepositMax: false,
  maximumDeposit: 0,
  threeDSAuthData: undefined,
  creditCardMessage: "",
  isSubmitting: false,
};

export const fetchPaymentReviewDetail = createAsyncThunk(
  "checkoutPayment/fetchPaymentReviewDetail",
  async (
    params: {
      registration: string;
      form_id: string;
    },
    { dispatch }
  ) => {
    dispatch(startLoading());
    try {
      const response = await checkoutPaymentClient.fetchDetail({
        form_id: params.form_id,
        registration: params.registration,
      });
      dispatch(endLoading());
      // The value we return becomes the `fulfilled` action payload
      return response;
    } catch (e) {
      dispatch(endLoading());
      dispatch(
        showNotification("Failed to load payment details, please try again.")
      );
    }
  }
);

export const checkoutPaymentSlice = createSlice({
  name: "checkoutPayment",
  initialState,
  reducers: {
    selectDekoOption: (state, action: PayloadAction<string>) => {
      state.dekoOptionSelected = action.payload;
    },
    setCardTypeText: (state, action: PayloadAction<string>) => {
      state.cardType = action.payload;
    },
    setLoanDeposit: (state, action: PayloadAction<number>) => {
      state.loanDeposit = action.payload;
    },
    setPlateFixture: (state, action: PayloadAction<string>) => {
      state.plateFixture = action.payload;
    },
    setPlateFixtureValue: (state, action: PayloadAction<number>) => {
      state.plateFixtureValue = action.payload;
    },
    clearThreeDSAuthData: (state) => {
      state.threeDSAuthData = undefined;
    },
    setFinancePrice: (state, action: PayloadAction<number>) => {
      state.financePrice = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPaymentReviewDetail.pending, (state) => {
      state.fetchingDetailStatus = true;
    });
    builder.addCase(fetchPaymentReviewDetail.fulfilled, (state, action) => {
      state.fetchingDetailStatus = false;
      state.formArr = (action.payload as any).data.formArr;
      state.registrationDetail = (
        action.payload as any
      ).data.registrationDetail;
      state.financeOptions = (action.payload as any).data.financeOptions;
      state.financePrice = (action.payload as any).data.financePrice;
      state.cardFeeArr = (action.payload as any).data.cardFeeArr;
      state.calcPrice = (action.payload as any).data.calcPrice;
      state.pricingArr = (action.payload as any).data.pricingArr;
      state.maximumDeposit = (action.payload as any).data.maximumDeposit;
      state.forceDepositMax = (action.payload as any).data.forceDepositMax;
      state.creditCardMessage = (action.payload as any).data.creditCardMessage;
    });
    builder.addCase(submitFinanceOption.fulfilled, (state, action) => {
      state.creditInformation.apr = (action.payload as any).apr;
      state.creditInformation.loan_cost = (action.payload as any).loan_cost;
      state.creditInformation.loan_repayment = (
        action.payload as any
      ).loan_repayment;
      state.creditInformation.payment_regular = (
        action.payload as any
      ).payment_regular;
      state.creditInformation.payment_regular_detail = (
        action.payload as any
      ).payment_regular_detail;
      state.creditInformation.annual_rate = (
        action.payload as any
      ).annual_rate;
    });
    builder.addCase(submitCheckoutPayment.pending, (state, action) => {
      state.isSubmitting = true;
    });
    builder.addCase(submitCheckoutPayment.fulfilled, (state, action) => {
      state.isSubmitting = false;
      const data = action.payload as any;
      if (data?.status === "3DAUTH") {
        state.threeDSAuthData = data;
      }
    });
  },
});

export const {
  selectDekoOption,
  setCardTypeText,
  setLoanDeposit,
  setPlateFixture,
  setPlateFixtureValue,
  clearThreeDSAuthData,
  setFinancePrice,
} = checkoutPaymentSlice.actions;

export const submitFinanceOption = createAsyncThunk(
  "checkoutPayment/submitFinanceOption",
  async (
    params: {
      amount: number;
      product: string;
      deposit: number;
      calculationResult?: {
        apr: string;
        annual_rate: string;
        payment_regular: string;
        payment_regular_detail: string;
        loan_repayment: string;
        loan_cost: string;
      };
    },
    { dispatch }
  ) => {
    dispatch(startLoading());

    // if calculationResult is provided, use it
    if (params.calculationResult) {
      dispatch(endLoading());
      return params.calculationResult;
    }

    // otherwise call API
    const response = await commonClient.financeOption({
      amount: params.amount,
      product: params.product,
      deposit: params.deposit,
    });
    dispatch(endLoading());
    return response;
  }
);

export const selectFormArr = (state: RootState) =>
  state.checkoutPayment.formArr;
export const selectRegistrationDetail = (state: RootState) =>
  state.checkoutPayment.registrationDetail;
export const selectFinanceOptions = (state: RootState) =>
  state.checkoutPayment.financeOptions;
export const selectFinancePrice = (state: RootState) =>
  state.checkoutPayment.financePrice;
export const selectCreditInformation = (state: RootState) =>
  state.checkoutPayment.creditInformation;
export const selectPlateFixture = (state: RootState) =>
  state.checkoutPayment.plateFixture;
export const selectPlateFixtureValue = (state: RootState) =>
  state.checkoutPayment.plateFixtureValue;
export const selectCalcPrice = (state: RootState) =>
  state.checkoutPayment.calcPrice;
export const selectFetchingStatus = (state: RootState) =>
  state.checkoutPayment.fetchingDetailStatus;
export const selectForceDepositMax = (state: RootState) =>
  state.checkoutPayment.forceDepositMax;
export const selectMaximumDeposit = (state: RootState) =>
  state.checkoutPayment.maximumDeposit;
export const selectThreeDSAuthData = (state: RootState) =>
  state.checkoutPayment.threeDSAuthData;
export const selectCreditCardMessage = (state: RootState) =>
  state.checkoutPayment.creditCardMessage;
export const selectIsSubmitting = (state: RootState) =>
  state.checkoutPayment.isSubmitting;

export const submitCheckoutPayment = createAsyncThunk(
  "checkoutPayment/submitCheckoutPayment",
  async (
    params: {
      isFinance: boolean;
      appel: string;
      firstName: string;
      surName: string;
      cardNumber: string;
      expiry: string;
      cvv: string;
      thirdPartyQualify: boolean;
      sendCommunciation: boolean;
      agreePolicy: boolean;
      continueCallback?: Function;
      finance_product_id?: string;
      finance_product_guid?: string;
      deposit?: number;
    },
    { dispatch, getState }
  ) => {
    const {
      appel,
      firstName,
      surName,
      cardNumber,
      expiry,
      cvv,
      thirdPartyQualify,
      sendCommunciation,
      agreePolicy,
      continueCallback,
    } = params;

    const currentState = getState() as RootState;
    const checkoutInfoState = currentState.checkoutInfo;
    const checkoutPaymentState = currentState.checkoutPayment;
    const registrationDetail = checkoutPaymentState.registrationDetail;
    const formArr = checkoutPaymentState.formArr;
    const pricingArr = checkoutPaymentState.pricingArr;
    const formId = checkoutInfoState.formValues.formId;
    const initialPaymentValue =
      parseFloat(checkoutPaymentState.cardFeeArr.price) !=
      checkoutPaymentState.cardFeeArr.initialPayment
        ? checkoutPaymentState.cardFeeArr.initialPayment
        : "";

    // delivery type & charge
    let deliveryTypeCharge;
    let deliveryType;
    if (
      pricingArr.deliveryPrice < 0.1 &&
      params.isFinance &&
      registrationDetail?.sourcecode === "1"
    ) {
      deliveryTypeCharge = "10.74";
      deliveryType = "standard";
    } else {
      deliveryTypeCharge = pricingArr.deliveryPrice;
      deliveryType = formArr?.delivery_type;
    }

    // total amount
    let totalAmount;
    let cardFee = formArr?.creditcardfee;
    let cardInput = "0.00";
    if (params.isFinance) {
      totalAmount = parseFloat(checkoutPaymentState.calcPrice.toString()) + parseFloat(checkoutPaymentState.plateFixtureValue.toString());
    } else {
      if (checkoutPaymentState.cardType === "American Express") {
        totalAmount = pence(
          checkoutPaymentState.calcPrice +
            checkoutPaymentState.cardFeeArr.amexFee +
            checkoutPaymentState.plateFixtureValue
        );
        cardFee = pence(checkoutPaymentState.cardFeeArr.amexFee).toString();
        cardInput = pence(checkoutPaymentState.cardFeeArr.amexFee).toString();
      } else if (checkoutPaymentState.cardType === "Visa Credit") {
        totalAmount = pence(
          checkoutPaymentState.calcPrice +
            parseFloat(checkoutPaymentState.cardFeeArr.creditFee) +
            checkoutPaymentState.plateFixtureValue
        );

        cardFee = pence(
          parseFloat(checkoutPaymentState.cardFeeArr.creditFee)
        ).toString();
        cardInput = pence(
          parseFloat(checkoutPaymentState.cardFeeArr.creditFee)
        ).toString();
      } else if (checkoutPaymentState.cardType === "maestro_charge") {
        totalAmount = pence(
          checkoutPaymentState.calcPrice +
            checkoutPaymentState.cardFeeArr.maestroFee +
            checkoutPaymentState.plateFixtureValue
        );
        cardFee = pence(checkoutPaymentState.cardFeeArr.maestroFee).toString();
        cardInput = pence(
          checkoutPaymentState.cardFeeArr.maestroFee
        ).toString();
      } else {
        totalAmount = pence(
          checkoutPaymentState.calcPrice +
            checkoutPaymentState.plateFixtureValue
        );
        console.log(totalAmount);
        cardFee = "0.00";
        cardInput = "0.00";
      }
    }

    const data = {
      finance: params.isFinance,
      registration: registrationDetail?.registration,
      // FormID: "59eb7c91f58e0c12f5190ea02567a5edf494f24a662fdb4",
      FormID: formId,
      maxpagenb: 2,
      incpagenb: 2,
      PageIndexVal: "",
      price: registrationDetail?.price,
      display_price: registrationDetail?.display_price,
      cardfeeinput: cardInput,
      totalcostinput: "&pound;" + checkoutPaymentState.calcPrice,
      deko_finance_value: params.isFinance
        ? checkoutPaymentState.calcPrice
        : parseFloat(checkoutPaymentState.calcPrice.toString()) +
          parseFloat(checkoutPaymentState.plateFixtureValue.toString()),
      fname: null,
      price_inc_transfer: registrationDetail?.price_inc_transfer,
      vat: registrationDetail?.vat,
      display_price_inc_transfer:
        registrationDetail?.display_price_inc_transfer,
      display_vat: registrationDetail?.display_vat,
      initial_payment: initialPaymentValue,
      remaining_payment: checkoutPaymentState.cardFeeArr.remainingPayment,
      calcprice: checkoutPaymentState.calcPrice,
      browserDetail: navigator.userAgent,
      payment_type: params.isFinance ? "full_finance" : "",
      v12_guid_selected: "",
      deko_option_selected: checkoutPaymentState.dekoOptionSelected,
      cardtype: checkoutPaymentState.cardType,
      loanDeposit: checkoutPaymentState.loanDeposit,
      plateFixtures: checkoutPaymentState.plateFixture,
      plateFixturesValue: checkoutPaymentState.plateFixtureValue,
      title: checkoutInfoState.formValues?.title ? checkoutInfoState.formValues.title : appel,
      forename: firstName,
      surname: surName,
      cardnumber: cardNumber,
      cardexpiry: expiry,
      signature_strip_number: cvv,
      fThirdPartyAuth: thirdPartyQualify ? "Yes" : "No",
      gift_or_surprise: sendCommunciation ? "Yes" : "No",
      distance_selling_warning: agreePolicy ? "I Agree" : "",
      adminCharge: registrationDetail?.discounted_web_admin_fee || "",
      premiumCharge: registrationDetail?.premiumCharge || "",
      totalcost: "&pound;" + registrationDetail?.price,
      creditcardfee: cardFee,
      initialPrice: formArr?.display_price,
      VATPrice: formArr?.display_vat,
      transferFee: registrationDetail?.transfer_fee,
      TransferService: 0,
      AcrylicPlates: formArr?.acrylic_plate_total,
      AluminiumPlates: "",
      CreditCardFee: formArr?.creditcardfee,
      issue_number: "",
      validfrom: "",
      deliveryTypeCharge,
      deliveryType,
      TotalAmountToPayNow: totalAmount,
      button: params.isFinance
        ? "Continue to Credit Application"
        : "Submit Payment",
      finance_product_id: params.finance_product_id,
      finance_product_guid: params.finance_product_guid,
      deposit: Number(params.deposit).toFixed(2),
    };
    dispatch(startLoading());
    try {
      const response: any = await checkoutPaymentClient.completeCheckoutPayment(
        data
      );
      dispatch(endLoading());
      if (response.data.status === "success") {
        // if redirect is not empty, open in new tab
        if (response.data.redirect) {
          window.open(response.data.redirect, '_blank');
        }
        params?.continueCallback && params?.continueCallback();
      }

      return response.data;
    } catch (e) {
      dispatch(endLoading());
      dispatch(showNotification(getErrorMessage(e)));
    }
  }
);

const pence = (amount: number) => {
  amount -= 0;
  amount = Math.round(amount * 100) / 100;
  return amount === Math.floor(amount)
    ? amount + ".00"
    : amount * 10 === Math.floor(amount * 10)
    ? amount + "0"
    : amount;
};

export default checkoutPaymentSlice.reducer;
